// src/utils/gtm/gtmApi.js

import axios from 'axios';
import { auth } from '../../firebase/init';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safemcc-portal-gateway-v2-bvn24355.ew.gateway.dev'

// Helper for getting Firebase auth token
const getAuthTokenForGtm = async () => {
    try {
        if (!auth.currentUser) {
            await new Promise(resolve => setTimeout(resolve, 1500)); 
            if (!auth.currentUser) {
                console.error('[gtmApi.js] getAuthTokenForGtm: No authenticated user after delay.');
                throw new Error('No authenticated user for GTM API');
            }
        }
        const token = await auth.currentUser.getIdToken(true);
        return token;
    } catch (error) {
        console.error('[gtmApi.js] Failed to get auth token for GTM');
        throw error;
    }
};

const createAxiosConfig = async (options = {}) => {
    let token;
    try {
        token = await getAuthTokenForGtm();
        return {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    } catch (error) {
        console.error('[gtmApi.js] Failed to create Axios config for GTM');
        throw error;
    }
};


/**
 * Fetches the list of GTM accounts from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM accounts.
 * @returns {Promise<Array>} Promise resolving to an array of GTM account objects.
 */
export const fetchAccountsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM accounts`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of users with access from the BigQuery table via the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the users with access.
 * @returns {Promise<Array>} Promise resolving to an array of user access objects.
 */
export const fetchUsersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM users`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM containers from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM containers.
 * @returns {Promise<Array>} Promise resolving to an array of GTM container objects.
 */
export const fetchContainersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-containers';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM containers`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM container users from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM container users.
 * @returns {Promise<Array>} Promise resolving to an array of GTM container user objects.
 */
export const fetchContainerUsersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-container-users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM container users`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM pending account-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM pending requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM pending request objects.
 */
export const fetchPendingRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-pending-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM pending requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM pending container-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM pending container requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM pending container request objects.
 */
export const fetchPendingContainerRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-pending-container-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM pending container requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM approved account-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM approved requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM approved request objects.
 */
export const fetchApprovedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-approved-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM approved requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM approved container-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM approved container requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM approved container request objects.
 */
export const fetchApprovedContainerRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-approved-container-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM approved container requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM rejected account-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM rejected requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM rejected request objects.
 */
export const fetchRejectedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-rejected-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM rejected requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM rejected container-level requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GTM rejected container requests.
 * @returns {Promise<Array>} Promise resolving to an array of GTM rejected container request objects.
 */
export const fetchRejectedContainerRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-rejected-container-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM rejected container requests`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM account-level audit logs from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs.
 * @returns {Promise<Array>} Promise resolving to an array of GTM audit log objects.
 */
export const fetchAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM audit logs`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GTM container-level audit logs from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the container audit logs.
 * @returns {Promise<Array>} Promise resolving to an array of GTM container audit log objects.
 */
export const fetchContainerAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gtm/gtm-container-audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error fetching GTM container audit logs`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates an access binding for a user in a GTM account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedFor, 
 *      requestedRole,
 *      requestDetails,
 *      and optionally batchRequests (for batch operations)
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/grant-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error creating access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates a container-level access binding for a user in a GTM container.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName,
 *      containerId,
 *      containerName,
 *      requestedFor, 
 *      requestedRole,
 *      requestDetails,
 *      and optionally batchRequests (for batch operations)
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createContainerAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/grant-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error creating container access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Removes an access binding for a user in a GTM account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedFor, 
 *      previousRole,
 *      path
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const removeAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/remove-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error removing access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Removes a container-level access binding for a user in a GTM container.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName,
 *      containerId,
 *      containerName,
 *      requestedFor, 
 *      previousRole,
 *      path
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const removeContainerAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/remove-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error removing container access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Modifies an access binding for a user in a GTM account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedFor, 
 *      requestedRole,
 *      previousRole,
 *      path
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const modifyAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/modify-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error modifying access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Modifies a container-level access binding for a user in a GTM container.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName,
 *      containerId,
 *      containerName,
 *      requestedFor, 
 *      requestedRole,
 *      previousRole,
 *      path
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const modifyContainerAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/modify-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error modifying container access binding`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Approves a user's pending account-level access request to a GTM account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      approvedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const approvePendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/approve-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error approving pending request`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Approves a user's pending container-level access request to a GTM container.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName,
 *      containerId,
 *      containerName,
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      approvedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const approveContainerPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/approve-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error approving container pending request`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Rejects a user's pending account-level access request to a GTM account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      rejectedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const rejectPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/reject-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error rejecting pending request`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Rejects a user's pending container-level access request to a GTM container.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName,
 *      containerId,
 *      containerName,
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRole, 
 *      requestedOn, 
 *      rejectedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const rejectContainerPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/reject-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error rejecting container pending request`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates a batch of access bindings for user(s) to GTM account(s). Uses same endpoint as createAccessBindingFromApi.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      requestedBy,
 *      requestDetails,
 *      batchRequests
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createBatchAccessBindingsFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/grant-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error creating batch access bindings`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates a batch of container-level access bindings for user(s) to GTM container(s). Uses same endpoint as createContainerAccessBindingFromApi.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      requestedBy,
 *      requestDetails,
 *      batchRequests
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createBatchContainerAccessBindingsFromApi = async (requestData) => {
    const endpointPath = '/api/gtm/grant-container-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[gtmApi.js] Error creating batch container access bindings`);
        console.error(`[gtmApi.js] Status: ${error.response?.status}`);
        console.error(`[gtmApi.js] Code: ${error.code}`);
        console.error(`[gtmApi.js] Message: ${error.message}`);
        console.error(`[gtmApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};
